import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MaintenanceStore } from 'core/stores/maintenance.store';
import { HealthCheckService } from 'core/services/health-check.service';

const POLL_INTERVAL_MS = 15_000;

@Component({
  selector: 'app-maintenance',
  standalone: true,
  imports: [CommonModule, MatIconModule, MatButtonModule],
  template: `
    <div class="maintenance">
      <img src="assets/images/logo.png" alt="Freely Payments" class="maintenance__logo"
           onerror="this.style.display='none'" />
      <mat-icon class="maintenance__icon">build_circle</mat-icon>
      <h1 class="maintenance__title">Freely Payments is under maintenance</h1>
      <p class="maintenance__message">
        {{ store.maintenanceReason() || 'We will be back online shortly.' }}
      </p>
      <p class="maintenance__hint">
        This page will automatically refresh once the service is available again.
      </p>
      <button mat-raised-button color="primary" (click)="retry()" [disabled]="busy">
        <mat-icon>refresh</mat-icon>
        Retry now
      </button>
    </div>
  `,
  styles: [`
    .maintenance {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      min-height: 100vh;
      text-align: center;
      padding: 24px;
      background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    }
    .maintenance__logo {
      max-width: 200px;
      margin-bottom: 24px;
    }
    .maintenance__icon {
      font-size: 96px;
      width: 96px;
      height: 96px;
      color: #f39c12;
      margin-bottom: 24px;
      animation: pulse 2s ease-in-out infinite;
    }
    .maintenance__title {
      font-size: 32px;
      font-weight: 600;
      margin: 0 0 16px 0;
      color: #333;
      max-width: 720px;
    }
    .maintenance__message {
      font-size: 18px;
      color: #555;
      margin: 0 0 12px 0;
      max-width: 640px;
    }
    .maintenance__hint {
      font-size: 14px;
      color: #888;
      margin: 0 0 32px 0;
      max-width: 640px;
    }
    @keyframes pulse {
      0%, 100% { transform: scale(1); opacity: 1; }
      50%      { transform: scale(1.05); opacity: 0.8; }
    }
  `],
})
export class MaintenanceComponent implements OnInit, OnDestroy {
  readonly store = inject(MaintenanceStore);
  private readonly healthCheck = inject(HealthCheckService);
  private readonly router = inject(Router);
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  busy = false;

  ngOnInit(): void {
    this.pollTimer = setInterval(() => this.poll(), POLL_INTERVAL_MS);
    // Fire one right away in case we just landed here from an interceptor
    this.poll();
  }

  ngOnDestroy(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
  }

  async retry(): Promise<void> {
    await this.poll();
  }

  private async poll(): Promise<void> {
    if (this.busy) return;
    this.busy = true;
    try {
      await this.healthCheck.check();
      if (!this.store.isUnderMaintenance()) {
        this.router.navigateByUrl('/');
      }
    } finally {
      this.busy = false;
    }
  }
}
